import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Form, Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Link, useNavigate } from 'react-router-dom';
import Flex from 'components/common/Flex';
import FalconCloseButton from 'components/common/FalconCloseButton';
import SoftBadge from 'components/common/SoftBadge';
import { useTranslation } from 'react-i18next';
import { useAuth } from 'context/AuthContext';
import axiosInstance from 'axiosInstance';
import {
  buildSlashCommandSuggestions,
  buildVisibleLauncherModules,
  filterVisibleModules,
  mapSearchResultToReference,
  parseScopedCommand,
  normalizeQuery
} from 'helpers/slashSearch';

const SEARCH_DEBOUNCE_MS = 2000;
const RECENT_SEARCHES_STORE_KEY = 'topSearchRecent';
const MAX_RECENT_SEARCHES = 6;

const getRecentSearches = () => {
  try {
    const rawValue = window.localStorage.getItem(RECENT_SEARCHES_STORE_KEY);
    const parsedValue = JSON.parse(rawValue || '[]');
    return Array.isArray(parsedValue) ? parsedValue : [];
  } catch (error) {
    return [];
  }
};

const saveRecentSearch = result => {
  if (!result?.url || !result?.title) return;
  const currentRecent = getRecentSearches().filter(item => item.url !== result.url);
  const nextRecent = [
    {
      title: result.title,
      subtitle: result.subtitle || '',
      url: result.url,
      icon: result.icon || 'history',
      label: result.label || '',
      image_url: result.image_url || ''
    }
  ].concat(currentRecent).slice(0, MAX_RECENT_SEARCHES);
  window.localStorage.setItem(RECENT_SEARCHES_STORE_KEY, JSON.stringify(nextRecent));
};

const SearchResultContent = ({ item, isActive, onMouseEnter, onSelect }) => (
  <Dropdown.Item
    className={classNames('px-x1 py-2', { 'bg-200': isActive })}
    as={item.url ? Link : 'button'}
    to={item.url || undefined}
    type={item.url ? undefined : 'button'}
    onMouseEnter={onMouseEnter}
    onMouseDown={event => {
      if (item.kind === 'slash_command') {
        event.preventDefault();
      }
    }}
    onClick={onSelect}
  >
    <Flex alignItems="center">
      {item.image_url ? (
        <div
          className="rounded-3 overflow-hidden border bg-white"
          style={{ width: '2.75rem', height: '2.75rem', flexShrink: 0 }}
        >
          <img
            src={item.image_url}
            alt={item.title || 'result'}
            className="w-100 h-100"
            style={{ objectFit: 'cover' }}
          />
        </div>
      ) : (
        <div
          className="rounded-circle d-flex align-items-center justify-content-center bg-soft-primary text-primary"
          style={{ width: '2.5rem', height: '2.5rem', flexShrink: 0 }}
        >
          <FontAwesomeIcon icon={item.icon || 'search'} />
        </div>
      )}
      <div className="ms-2 flex-1" style={{ minWidth: 0 }}>
        <div className="d-flex align-items-center gap-2">
          <h6 className="mb-0 text-truncate">{item.title}</h6>
          {item.label ? (
            <SoftBadge bg="primary" className="fw-medium text-decoration-none">
              {item.label}
            </SoftBadge>
          ) : null}
        </div>
        <p className="fs--2 mb-0 text-600 text-truncate">
          {item.subtitle}
        </p>
      </div>
    </Flex>
  </Dropdown.Item>
);

const SearchBox = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission, hasAnyPermission, userData } = useAuth();
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [contentResults, setContentResults] = useState([]);
  const [contentLoading, setContentLoading] = useState(false);
  const [contentLoadedQuery, setContentLoadedQuery] = useState('');
  const [recentSearches, setRecentSearches] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const debounceRef = useRef(null);
  const inputRef = useRef(null);

  const visibleModules = useMemo(
    () => buildVisibleLauncherModules({ hasPermission, hasAnyPermission, userData }),
    [hasAnyPermission, hasPermission, userData]
  );

  const parsedCommandQuery = useMemo(
    () => parseScopedCommand(searchInputValue, visibleModules, t),
    [searchInputValue, t, visibleModules]
  );

  const moduleResults = useMemo(() => {
    const query = parsedCommandQuery.module ? '' : parsedCommandQuery.contentQuery;
    if (parsedCommandQuery.isSlashCommand) {
      return [];
    }
    if (!query) {
      return visibleModules.slice(0, 6).map(module => ({
        id: module.id,
        url: module.link,
        title: module.titleKey.includes('.') ? t(module.titleKey) : module.titleKey,
        subtitle: (module.keywords || []).slice(0, 3).join(' · '),
        icon: module.icon,
        label: t('SEARCHBOX.BADGES.MODULE')
      }));
    }

    return filterVisibleModules(visibleModules, query, t)
      .map(module => ({
        id: module.id,
        url: module.link,
        title: module.titleKey.includes('.') ? t(module.titleKey) : module.titleKey,
        subtitle: (module.keywords || []).slice(0, 4).join(' · '),
        icon: module.icon,
        label: t('SEARCHBOX.BADGES.MODULE')
      }))
      .slice(0, 8);
  }, [parsedCommandQuery.contentQuery, parsedCommandQuery.isSlashCommand, parsedCommandQuery.module, t, visibleModules]);

  const slashSuggestions = useMemo(() => {
    if (!parsedCommandQuery.isSlashCommand || parsedCommandQuery.module || parsedCommandQuery.contentQuery) {
      return [];
    }

    return buildSlashCommandSuggestions(
      visibleModules,
      t,
      parsedCommandQuery.commandQuery
    ).slice(0, 8);
  }, [
    parsedCommandQuery.commandQuery,
    parsedCommandQuery.contentQuery,
    parsedCommandQuery.isSlashCommand,
    parsedCommandQuery.module,
    t,
    visibleModules
  ]);

  const flattenedResults = useMemo(() => {
    const mergedResults = [];
    if (!normalizeQuery(searchInputValue)) {
      recentSearches.forEach(item => mergedResults.push(item));
    }
    slashSuggestions.forEach(item => mergedResults.push(item));
    moduleResults.forEach(item => mergedResults.push(item));
    contentResults.forEach(item =>
      mergedResults.push({
        ...item,
        icon: item.icon || 'search',
        label: item.section_label || t('SEARCHBOX.BADGES.RESULT')
      })
    );
    return mergedResults;
  }, [contentResults, moduleResults, recentSearches, searchInputValue, slashSuggestions, t]);

  useEffect(() => {
    setRecentSearches(getRecentSearches());
  }, []);

  useEffect(() => {
    setActiveIndex(0);
  }, [searchInputValue, contentResults, moduleResults]);

  useEffect(() => {
    const query = parsedCommandQuery.contentQuery;

    if (debounceRef.current) {
      window.clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }

    if (!query) {
      setContentResults([]);
      setContentLoadedQuery('');
      setContentLoading(false);
      return undefined;
    }

    debounceRef.current = window.setTimeout(async () => {
      if (query.replace(/\*/g, '').trim().length < 2) {
        setContentResults([]);
        setContentLoadedQuery(query);
        setContentLoading(false);
        return;
      }

      setContentLoading(true);
      try {
        const response = await axiosInstance.get('modules/search/global', {
          params: {
            q: query,
            section: parsedCommandQuery.module?.id || ''
          }
        });
        setContentResults(
          (response.data?.results || []).map(item => ({
            ...mapSearchResultToReference(item),
            label: item.section_label || t('SEARCHBOX.BADGES.RESULT')
          }))
        );
        setContentLoadedQuery(query);
      } catch (error) {
        console.error('Global search error:', error);
        setContentResults([]);
        setContentLoadedQuery(query);
      } finally {
        setContentLoading(false);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) {
        window.clearTimeout(debounceRef.current);
        debounceRef.current = null;
      }
      return undefined;
    };
  }, [parsedCommandQuery.contentQuery, parsedCommandQuery.module, t]);

  const firstActionableResult = flattenedResults[activeIndex] || flattenedResults[0] || null;

  const clearSearch = () => {
    setSearchInputValue('');
    setContentResults([]);
    setContentLoadedQuery('');
    setDropdownOpen(false);
    setActiveIndex(0);
  };

  const handleSelectResult = result => {
    if (!result?.url) return;
    saveRecentSearch(result);
    setRecentSearches(getRecentSearches());
    clearSearch();
    if (inputRef.current) {
      inputRef.current.blur();
    }
    navigate(result.url);
  };

  const handleSelectSlashSuggestion = result => {
    if (!result?.command) return;
    setSearchInputValue(`/${result.command} `);
    setDropdownOpen(true);
    setActiveIndex(0);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleKeyDown = event => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (!flattenedResults.length) return;
      setDropdownOpen(true);
      setActiveIndex(currentValue => (currentValue + 1) % flattenedResults.length);
      return;
    }

    if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (!flattenedResults.length) return;
      setDropdownOpen(true);
      setActiveIndex(currentValue => (currentValue - 1 + flattenedResults.length) % flattenedResults.length);
      return;
    }

    if (event.key === 'Escape') {
      clearSearch();
      return;
    }

    if (event.key !== 'Enter') {
      return;
    }

    if (firstActionableResult?.kind === 'slash_command') {
      event.preventDefault();
      handleSelectSlashSuggestion(firstActionableResult);
      return;
    }

    if (parsedCommandQuery.module && !parsedCommandQuery.contentQuery) {
      event.preventDefault();
      handleSelectResult({
        url: parsedCommandQuery.module.link,
        title: t(parsedCommandQuery.module.titleKey),
        subtitle: (parsedCommandQuery.module.keywords || []).slice(0, 3).join(' · '),
        icon: parsedCommandQuery.module.icon,
        label: t('SEARCHBOX.BADGES.MODULE')
      });
      return;
    }

    if (!firstActionableResult) {
      return;
    }

    event.preventDefault();
    handleSelectResult(firstActionableResult);
  };

  return (
    <Dropdown show={dropdownOpen} className="search-box">
      <Form className="position-relative">
        <Form.Control
          ref={inputRef}
          type="search"
          placeholder={t('SEARCHBOX.PLACEHOLDER')}
          aria-label="Search"
          className="rounded-pill search-input"
          value={searchInputValue}
          onChange={({ target }) => setSearchInputValue(target.value)}
          onFocus={() => {
            setDropdownOpen(true);
            setRecentSearches(getRecentSearches());
          }}
          onKeyDown={handleKeyDown}
          onBlur={() => window.setTimeout(() => setDropdownOpen(false), 150)}
        />
        <FontAwesomeIcon
          icon="search"
          className="position-absolute text-400 search-box-icon"
        />
        {(dropdownOpen || searchInputValue) && (
          <div className="search-box-close-btn-container">
            <FalconCloseButton
              size="sm"
              noOutline
              className="fs--2"
              onClick={() => {
                clearSearch();
              }}
            />
          </div>
        )}
      </Form>
      <Dropdown.Menu>
        <div className="scrollbar py-3" style={{ maxHeight: '24rem' }}>
          {!normalizeQuery(searchInputValue) && recentSearches.length ? (
            <>
              <Dropdown.Header as="h6" className="px-x1 pt-0 pb-2 fw-medium">
                {t('SEARCHBOX.SECTIONS.RECENT')}
              </Dropdown.Header>
              {recentSearches.map((item, index) => (
                <SearchResultContent
                  item={item}
                  key={`${item.url}-${index}`}
                  isActive={index === activeIndex}
                  onMouseEnter={() => setActiveIndex(index)}
                  onSelect={() => handleSelectResult(item)}
                />
              ))}
              <hr className="text-200 dark__text-900" />
            </>
          ) : null}

          {slashSuggestions.length ? (
            <>
              <Dropdown.Header as="h6" className="px-x1 pt-0 pb-2 fw-medium">
                {t('SEARCHBOX.SECTIONS.SLASH')}
              </Dropdown.Header>
              {slashSuggestions.map((item, index) => (
                <SearchResultContent
                  item={item}
                  key={item.id}
                  isActive={recentSearches.length + index === activeIndex}
                  onMouseEnter={() => setActiveIndex(recentSearches.length + index)}
                  onSelect={() => handleSelectSlashSuggestion(item)}
                />
              ))}
              <hr className="text-200 dark__text-900" />
            </>
          ) : null}

          {parsedCommandQuery.module && (
            <>
              <Dropdown.Header as="h6" className="px-x1 pt-0 pb-2 fw-medium">
                {t('SEARCHBOX.SECTIONS.SCOPE')}
              </Dropdown.Header>
              <div className="px-x1 py-2 fs--1 text-600 d-flex align-items-center gap-2">
                <SoftBadge bg="info" className="fw-medium text-decoration-none">
                  /{parsedCommandQuery.module.command}
                </SoftBadge>
                <span>{t(parsedCommandQuery.module.labelKey || parsedCommandQuery.module.titleKey)}</span>
              </div>
              <hr className="text-200 dark__text-900" />
            </>
          )}

          {moduleResults.length ? (
            <>
              <Dropdown.Header as="h6" className="px-x1 pt-0 pb-2 fw-medium">
                {t('SEARCHBOX.SECTIONS.MODULES')}
              </Dropdown.Header>
              {moduleResults.map((item, index) => (
                <SearchResultContent
                  item={item}
                  key={item.id}
                  isActive={recentSearches.length + slashSuggestions.length + index === activeIndex}
                  onMouseEnter={() => setActiveIndex(recentSearches.length + slashSuggestions.length + index)}
                  onSelect={() => handleSelectResult(item)}
                />
              ))}
              <hr className="text-200 dark__text-900" />
            </>
          ) : null}

          {contentLoading ? (
            <>
              <Dropdown.Header as="h6" className="px-x1 pt-0 pb-2 fw-medium">
                {t('SEARCHBOX.SECTIONS.CONTENTS')}
              </Dropdown.Header>
              <div className="px-x1 py-2 fs--1 text-600 d-flex align-items-center gap-2">
                <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
              </div>
            </>
          ) : null}

          {contentResults.length ? (
            <>
              <Dropdown.Header as="h6" className="px-x1 pt-0 pb-2 fw-medium">
                {t('SEARCHBOX.SECTIONS.CONTENTS')}
              </Dropdown.Header>
              {contentResults.map((item, index) => (
                <SearchResultContent
                  item={item}
                  key={item.id}
                  isActive={recentSearches.length + slashSuggestions.length + moduleResults.length + index === activeIndex}
                  onMouseEnter={() => setActiveIndex(recentSearches.length + slashSuggestions.length + moduleResults.length + index)}
                  onSelect={() => handleSelectResult(item)}
                />
              ))}
            </>
          ) : null}
        </div>
        <div className="text-center mt-n3">
          <p
            className={classNames('fs-1 fw-bold text-center', {
              'd-none': moduleResults.length > 0 || contentResults.length > 0 || contentLoading
            })}
          >
            {normalizeQuery(searchInputValue)
              ? (
                  contentLoadedQuery === normalizeQuery(searchInputValue)
                    ? t('SEARCHBOX.EMPTY')
                    : ''
                )
              : t('SEARCHBOX.START_TYPING')}
          </p>
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
};

SearchResultContent.propTypes = {
  item: PropTypes.shape({
    url: PropTypes.string,
    title: PropTypes.string,
    subtitle: PropTypes.string,
    icon: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    label: PropTypes.string
  }).isRequired,
  isActive: PropTypes.bool,
  onMouseEnter: PropTypes.func,
  onSelect: PropTypes.func
};

export default SearchBox;
